import React, {
  createContext,
  useContext,
  useState,
  useCallback,
  useEffect,
  useRef,
  useMemo,
} from 'react';
import type { ReactNode } from 'react';
import { doc, onSnapshot } from 'firebase/firestore';
import { FirebaseError } from '@firebase/util';
import { db } from '../api/firebase/config';
import {
  type UploadTask,
  type UploadParams,
  uploadTaskStatus,
  uploadTaskType,
} from '../types/upload';
import { ocrStatus } from '../constants/enum';
import { useUploadExecutor } from '../hooks/useUploadExecutor';

// --- State Context（タスク状態、頻繁に更新される） ---
interface UploadStateContextType {
  tasks: UploadTask[];
  hasActiveTasks: boolean;
  hasActiveTasksFor: (type: UploadTask['type'], patternId?: string) => boolean;
}

// --- Dispatch Context（アクション、安定した参照） ---
interface UploadDispatchContextType {
  startUpload: (params: UploadParams) => void;
  dismissTask: (taskId: string) => void;
  clearCompletedTasks: () => void;
  /** ref経由でアクティブタスクの有無を取得（再レンダリングを発生させない） */
  getHasActiveTasksFor: (type: UploadTask['type'], patternId?: string) => boolean;
}

const UploadStateContext = createContext<UploadStateContextType | undefined>(undefined);
const UploadDispatchContext = createContext<UploadDispatchContextType | undefined>(undefined);

interface UploadProviderProps {
  children: ReactNode;
}

export const UploadProvider: React.FC<UploadProviderProps> = ({ children }) => {
  const [tasks, setTasks] = useState<UploadTask[]>([]);
  const { executeUpload } = useUploadExecutor();
  const tasksRef = useRef(tasks);
  tasksRef.current = tasks;

  // onCompletedコールバックをタスクIDごとに管理
  const onCompletedCallbacksRef = useRef<Map<string, () => Promise<void>>>(new Map());
  // 各resultの完了状態をトラッキング（重複インクリメント防止、エフェクト再実行でも保持）
  const completedResultIdsRef = useRef<Set<string>>(new Set());

  const hasActiveTasks = tasks.some(
    t => t.status !== uploadTaskStatus.completed && t.status !== uploadTaskStatus.error,
  );

  const hasActiveTasksFor = useCallback(
    (type: UploadTask['type'], patternId?: string): boolean =>
      tasks.some(
        t =>
          t.type === type &&
          (patternId === undefined || t.patternId === patternId) &&
          t.status !== uploadTaskStatus.completed &&
          t.status !== uploadTaskStatus.error,
      ),
    [tasks],
  );

  // タブ閉じ時の警告
  useEffect(() => {
    const handleBeforeUnload = (e: BeforeUnloadEvent): void => {
      const active = tasksRef.current.some(
        t => t.status !== uploadTaskStatus.completed && t.status !== uploadTaskStatus.error,
      );
      if (active) {
        e.preventDefault();
      }
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, []);

  // processingステータスのタスクIDリストをメモ化（無限ループ防止）
  const processingTaskIds = useMemo(
    () =>
      tasks
        .filter(t => t.status === uploadTaskStatus.processing)
        .map(t => t.id)
        .join(','),
    [tasks],
  );

  // Firestoreリスナーでresultのocr_statusを監視
  useEffect(() => {
    if (!processingTaskIds) return;

    const currentTasks = tasksRef.current.filter(
      t => t.status === uploadTaskStatus.processing && t.resultIds && t.resultIds.length > 0,
    );

    const unsubscribes: (() => void)[] = [];

    currentTasks.forEach(task => {
      if (!task.resultIds) return;

      task.resultIds.forEach(resultId => {
        // 既に処理済みのresultはリスナー不要
        const trackingKey = `${task.id}_${resultId}`;
        if (completedResultIdsRef.current.has(trackingKey)) return;

        const resultDocRef = doc(db, 'results', resultId);
        const unsubscribe = onSnapshot(
          resultDocRef,
          snapshot => {
            if (!snapshot.exists()) return;
            const data = snapshot.data();
            const status = data?.ocr_status as number | undefined;

            // 既に処理済みのresultは無視
            if (completedResultIdsRef.current.has(trackingKey)) return;

            if (status === ocrStatus.completed || status === ocrStatus.error) {
              completedResultIdsRef.current.add(trackingKey);
              const isError = status === ocrStatus.error;

              // setTasks updater内で完了判定し、完了時はコールバックをスケジュール
              // （updater内で正確なprev stateを参照することでレースコンディションを防止）
              setTasks(prev =>
                prev.map(t => {
                  if (t.id !== task.id) return t;
                  const newProcessedCount = (t.processedCount || 0) + 1;
                  const newErrorCount = (t.errorResultCount || 0) + (isError ? 1 : 0);
                  const total = t.totalResultCount || 0;
                  const isAllCompleted = newProcessedCount >= total;

                  if (isAllCompleted) {
                    const hasErrors = newErrorCount > 0;
                    // 完了コールバックをスケジュール（state更新バッチ完了後に実行）
                    const callback = onCompletedCallbacksRef.current.get(task.id);
                    if (callback) {
                      onCompletedCallbacksRef.current.delete(task.id);
                      setTimeout(() => {
                        void callback().catch(() => {
                          // コンポーネントがアンマウント済みの場合は無視
                        });
                      }, 0);
                    }
                    return {
                      ...t,
                      status: hasErrors ? uploadTaskStatus.error : uploadTaskStatus.completed,
                      processedCount: newProcessedCount,
                      errorResultCount: newErrorCount,
                      errorMessage: hasErrors
                        ? `${newErrorCount}件の読取でエラーが発生しました`
                        : undefined,
                    };
                  }
                  return {
                    ...t,
                    processedCount: newProcessedCount,
                    errorResultCount: newErrorCount,
                  };
                }),
              );

              // このresultのリスナーは解除
              unsubscribe();
            }
          },
          error => {
            // ログアウト直後にリスナーが permission-denied を受け取るのは正常な動作
            if (error instanceof FirebaseError && error.code === 'permission-denied') {
              return;
            }
          },
        );
        unsubscribes.push(unsubscribe);
      });
    });

    return () => {
      unsubscribes.forEach(unsub => unsub());
    };
  }, [processingTaskIds]);

  const updateTask = useCallback((taskId: string, update: Partial<UploadTask>) => {
    setTasks(prev => prev.map(t => (t.id === taskId ? { ...t, ...update } : t)));
  }, []);

  const startUpload = useCallback(
    (params: UploadParams) => {
      const taskId = crypto.randomUUID();
      const fileCount = params.files.length;
      const displayCount = params.totalPageCount;
      const fileNames = params.pageFileNames;
      const label =
        params.type === 'classification'
          ? `自動仕分 ${displayCount}件`
          : `${params.patternName} ${displayCount}件`;

      const newTask: UploadTask = {
        id: taskId,
        type:
          params.type === 'classification' ? uploadTaskType.classification : uploadTaskType.pattern,
        status: uploadTaskStatus.uploading,
        label,
        fileNames,
        fileCount,
        totalPageCount: displayCount,
        uploadedCount: 0,
        patternId: params.type === 'pattern' ? params.patternId : undefined,
        hidePanel: params.type === 'pattern' ? params.hidePanel : undefined,
        createdAt: Date.now(),
      };

      setTasks(prev => [...prev, newTask]);

      // onCompletedコールバックを保存（processing → completed遷移時に呼び出す）
      if (params.type === 'pattern' && params.onCompleted) {
        onCompletedCallbacksRef.current.set(taskId, params.onCompleted);
      }

      // 非同期で実行（awaitしない）
      void executeUpload(params, (update: Partial<UploadTask>) => {
        updateTask(taskId, update);
      });
    },
    [executeUpload, updateTask],
  );

  const dismissTask = useCallback((taskId: string) => {
    setTasks(prev => prev.filter(t => t.id !== taskId));
  }, []);

  const clearCompletedTasks = useCallback(() => {
    setTasks(prev =>
      prev.filter(
        t => t.status !== uploadTaskStatus.completed && t.status !== uploadTaskStatus.error,
      ),
    );
  }, []);

  // ref経由でアクティブタスクを確認（再レンダリングを発生させない）
  const getHasActiveTasksFor = useCallback(
    (type: UploadTask['type'], patternId?: string): boolean =>
      tasksRef.current.some(
        t =>
          t.type === type &&
          (patternId === undefined || t.patternId === patternId) &&
          t.status !== uploadTaskStatus.completed &&
          t.status !== uploadTaskStatus.error,
      ),
    [],
  );

  // Dispatch contextは安定した参照を持つ（startUpload等はuseCallbackで安定化済み）
  const dispatchValue = useMemo(
    () => ({ startUpload, dismissTask, clearCompletedTasks, getHasActiveTasksFor }),
    [startUpload, dismissTask, clearCompletedTasks, getHasActiveTasksFor],
  );

  return (
    <UploadDispatchContext.Provider value={dispatchValue}>
      <UploadStateContext.Provider value={{ tasks, hasActiveTasks, hasActiveTasksFor }}>
        {children}
      </UploadStateContext.Provider>
    </UploadDispatchContext.Provider>
  );
};

/**
 * タスク状態を購読するフック（頻繁に再レンダリングされる）
 * UploadProgressPanel等、タスク状態の表示に使用
 */
export const useUploadState = (): UploadStateContextType => {
  const context = useContext(UploadStateContext);
  if (context === undefined) {
    throw new Error('useUploadState must be used within an UploadProvider');
  }
  return context;
};

/**
 * アクション（dispatch）のみ購読するフック（安定した参照、再レンダリングされにくい）
 * PatternAiOcr等、startUploadのみ必要なコンポーネントに使用
 */
export const useUploadDispatch = (): UploadDispatchContextType => {
  const context = useContext(UploadDispatchContext);
  if (context === undefined) {
    throw new Error('useUploadDispatch must be used within an UploadProvider');
  }
  return context;
};

/**
 * 後方互換：両方のcontextをまとめて返す（既存のuseUpload利用箇所用）
 * 注意: tasksの頻繁な更新で再レンダリングされるため、
 *       dispatch のみ必要な場合は useUploadDispatch を使用すること
 */
export const useUpload = (): UploadStateContextType & UploadDispatchContextType => {
  const state = useUploadState();
  const dispatch = useUploadDispatch();
  return { ...state, ...dispatch };
};
